import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import { Box, Drawer, IconButton, Typography } from "@mui/material";
import { IoClose } from "react-icons/io5";
import { HiOutlineLightBulb } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import MaButton from "../../../../Components/UI/ma-button";
import RsModal from "../../../../Components/UI/ma-modal";
import StatusChip from "./status-chip";
import { getSellerProductDetail } from "./seller-product-dummies";
import { DELETE_PRODUCT } from "../../../../Utils/Mutations";

function isPendingApprovalListingStatus(status) {
  const s = String(status ?? "")
    .trim()
    .toUpperCase();
  if (!s) return false;
  if (s.includes("REJECT")) return false;
  if (s.includes("APPROV") && !s.includes("PEND")) return false;
  if (s.includes("OUT OF STOCK") || s.includes("OUTOFSTOCK")) return false;
  if (s.includes("CREATED")) return true;
  if (s.includes("PEND")) return true;
  return false;
}

function DetailRow({ label, children, alignTop }) {
  return (
    <div
      className={`flex flex-col gap-1 py-4 first:pt-0 border-b border-[#F0F0F0] last:border-b-0 ${
        alignTop
          ? ""
          : "sm:flex-row sm:items-center sm:justify-between sm:gap-6"
      }`}
    >
      <Typography
        component="span"
        className="text-[14px] text-[#626262] shrink-0 sm:min-w-[120px]"
      >
        {label}
      </Typography>
      <div className="flex-1 text-right sm:text-right">{children}</div>
    </div>
  );
}

/**
 * Product Details — right-side drawer (read-only), unicorn-style UI.
 */
export default function ProductViewDrawer({
  open,
  onClose,
  productId,
  detail: detailProp,
  onStockUp,
  onProductDeleted,
  enableDelete = true,
}) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [activeThumb, setActiveThumb] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const [deleteProduct] = useMutation(DELETE_PRODUCT);

  const detail = useMemo(() => {
    if (detailProp) return detailProp;
    if (productId != null) return getSellerProductDetail(productId);
    return null;
  }, [productId, detailProp]);

  useEffect(() => {
    setActiveThumb(0);
  }, [productId, detailProp?.id]);

  useEffect(() => {
    if (!open) {
      setDeleteModalOpen(false);
      setDeleteError("");
      setDeleteSubmitting(false);
    }
  }, [open]);

  const galleryImages = (detail?.images || []).filter(Boolean);
  const mainSrc = galleryImages[activeThumb] ?? galleryImages[0] ?? null;

  const isPendingApproval = isPendingApprovalListingStatus(
    detail?.productListingStatus,
  );

  if (!detail) return null;

  const handleEdit = () => {
    onClose?.();
    navigate(`/seller/product/edit/${detail.id}`);
  };

  const handleStockUp = () => {
    onStockUp?.({
      id: detail.id,
      name: detail.name,
      stock: detail.stock,
    });
  };

  const handleConfirmDelete = async () => {
    if (!detail?.id) return;
    setDeleteError("");
    setDeleteSubmitting(true);
    try {
      await deleteProduct({
        variables: { productId: String(detail.id) },
      });
      enqueueSnackbar("Product deleted.", { variant: "success" });
      setDeleteModalOpen(false);
      onClose?.();
      onProductDeleted?.();
    } catch (e) {
      const msg =
        e?.graphQLErrors?.[0]?.message ||
        e?.networkError?.message ||
        e?.message ||
        "Could not delete product.";
      setDeleteError(msg);
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 560 },
          maxWidth: "100%",
          borderRadius: 0,
          boxShadow: "-8px 0 40px rgba(15, 23, 42, 0.08)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          bgcolor: "#FFFFFF",
        }}
      >
        {/* Header */}
        <header
          style={{ borderBottom: "1px solid #E5E4E2" }}
          className="flex shrink-0 items-center justify-between border-b border-[#EFEFEF] px-5 py-4"
        >
          <h2 className="my-0 text-[17px] font-semibold text-[#1A1A1A]">
            Product Details
          </h2>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close"
            sx={{
              bgcolor: "#F7F7F5",
              color: "#DC2626",
              borderRadius: "10px",
              "&:hover": { bgcolor: "#FECACA" },
            }}
          >
            <IoClose className="text-xl" />
          </IconButton>
        </header>

        {/* Scrollable body */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 3 }}>
          {/* Gallery */}
          <div className="mb-8">
            <div className="aspect-[16/10] overflow-hidden rounded-xl bg-[#F3F4F6] ring-1 ring-[#EEF0F3]">
              {mainSrc ? (
                <img
                  src={mainSrc}
                  alt=""
                  className="h-full w-full object-cover object-center"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-[13px] text-[#9CA3AF]">
                  No image
                </div>
              )}
            </div>
            {galleryImages.length > 0 ? (
              <div className="mt-3 flex gap-2">
                {galleryImages.map((src, i) => (
                  <div
                    key={`${src}-${i}`}
                    style={{
                      border: activeThumb === i ? "1px solid #2E1834" : "none",
                    }}
                    onClick={() => setActiveThumb(i)}
                    className={`h-16 w-16 box-border shrink-0 overflow-hidden rounded-lg border-2 transition-all `}
                  >
                    <img
                      src={src}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {/* Summary */}
          <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div className="flex-1">
              <h3 className="my-0 text-[20px] font-semibold leading-snug text-[#3D3D3D]">
                {detail.name}
              </h3>
              <p className="my-0 mt-1 text-[11px] text-[#626262]">
                {detail.sku}
                <span className="mx-2 text-[#626262]">·</span>
                {detail.category}
              </p>
            </div>
            <p className="my-0 text-[22px] font-black tracking-tight text-[#3D3D3D]">
              {detail.price}
            </p>
          </div>

          {isPendingApproval ? (
            <div className="mb-6 rounded-2xl border border-amber-200/80 bg-amber-50 p-4 md:p-5">
              <div className="flex gap-3">
                <HiOutlineLightBulb
                  className="mt-0.5 shrink-0 text-[24px] text-amber-500"
                  aria-hidden
                />
                <div>
                  <p className="my-0 text-[13px] font-semibold text-[#92400E]">
                    Awaiting Admin Approvals
                  </p>
                  <p className="my-0 mt-1 text-[12px] leading-relaxed text-[#A16207]">
                    You&apos;ll be notified once it&apos;s approved or if
                    changes are required.
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          {/* Attributes */}
          <section
            style={{ border: "1px solid #F3F4F6" }}
            className="rounded-[16px] border px-4 py-4"
          >
            <DetailRow label="Category">
              <span className="text-[14px] font-semibold text-[#1A1A1A]">
                {detail.categoryDetail}
              </span>
            </DetailRow>
            <DetailRow label="SKU">
              <span className="text-[14px] font-semibold text-[#1A1A1A]">
                {detail.sku}
              </span>
            </DetailRow>
            <div className="border-b flex justify-between border-[#F0F0F0] py-4 last:border-b-0 gap-x-5">
              <Typography className="mb-2 text-[13px] font-medium text-[#666666]">
                Description
              </Typography>
              <p className="my-0 text-[14px] leading-relaxed text-[#374151]">
                {detail.description}
              </p>
            </div>
            <DetailRow label="Price">
              <span className="text-[14px] font-semibold text-[#1A1A1A]">
                {detail.price}
              </span>
            </DetailRow>
            <DetailRow label="Stock">
              <span className="text-[14px] font-semibold text-[#1A1A1A]">
                {detail.stock} Units
              </span>
            </DetailRow>
            <DetailRow label="Stock Status">
              <div className="flex justify-end sm:justify-end">
                <StatusChip status={detail.status} />
              </div>
            </DetailRow>
            <DetailRow label="Weight">
              <span className="text-[14px] font-semibold text-[#1A1A1A]">
                {detail.weight}
              </span>
            </DetailRow>
            <DetailRow label="Total Orders">
              <span className="text-[14px] font-semibold text-[#1A1A1A]">
                {detail.totalOrders}
              </span>
            </DetailRow>
          </section>
        </Box>

        {/* Footer actions */}
        <footer className="shrink-0 border-t border-[#EFEFEF] bg-white px-4 py-4">
          {isPendingApproval ? (
            <div
              className={`grid grid-cols-1 gap-2 sm:gap-3 ${
                enableDelete ? "sm:grid-cols-2" : "sm:grid-cols-1"
              }`}
            >
              {enableDelete ? (
                <MaButton
                  title="Delete Product"
                  onClick={() => setDeleteModalOpen(true)}
                  sx={{
                    bgcolor: "#F3F4F6",
                    color: "#DC2626",
                    border: "none",
                    boxShadow: "none",
                    px: 2,
                    fontWeight: 600,
                    borderRadius: "999px",
                    "&:hover": { bgcolor: "#E5E7EB" },
                  }}
                />
              ) : null}
              <MaButton
                variant="primary"
                title="Edit Product"
                onClick={handleEdit}
                sx={{
                  fontWeight: 600,
                  borderRadius: "999px",
                  bgcolor: "#1A1625",
                  "&:hover": { bgcolor: "#2E1834" },
                }}
              />
            </div>
          ) : (
            <div
              className={`grid grid-cols-3  gap-3 ${
                enableDelete ? "sm:grid-cols-3" : "sm:grid-cols-2"
              }`}
            >
              {enableDelete ? (
                <MaButton
                  title="Delete Product"
                  onClick={() => setDeleteModalOpen(true)}
                  sx={{
                    bgcolor: "#F3F4F6",
                    color: "#DC2626",
                    border: "none",
                    boxShadow: "none",
                    px: 2,
                    fontWeight: 600,
                    "&:hover": { bgcolor: "#E5E7EB" },
                  }}
                />
              ) : null}
              <MaButton
                title="Edit Product"
                onClick={handleEdit}
                sx={{
                  bgcolor: "#F3F4F6",
                  color: "#1F2937",
                  border: "none",
                  boxShadow: "none",

                  px: 2,
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#E5E7EB" },
                }}
              />
              <MaButton
                variant="primary"
                title="Stock Up"
                onClick={handleStockUp}
                sx={{
                  fontWeight: 600,
                  bgcolor: "#2E1834",
                }}
              />
            </div>
          )}
        </footer>
      </Box>

      {enableDelete ? (
        <RsModal
          isOpen={deleteModalOpen}
          onClose={() => !deleteSubmitting && setDeleteModalOpen(false)}
        >
          <div className="w-[min(420px,calc(100vw-32px))] overflow-hidden rounded-[16px] bg-white p-0 shadow-xl outline-none">
            <div className="border-b border-[#EFEFEF] px-6 py-4">
              <h2
                id="delete-product-modal-title"
                className="my-0 text-[17px] text-center font-semibold text-[#1A1A1A]"
              >
                Delete product?
              </h2>
              <p className="my-0 mt-1 text-[13px] leading-snug text-center text-[#6B7280]">
                This will permanently remove the listing. You can&apos;t undo
                this action.
              </p>
            </div>

            <div className="flex gap-4 border-b border-[#F3F4F6] px-6 py-5">
              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-[#F3F4F6] ring-1 ring-[#EEF0F3]">
                {mainSrc ? (
                  <img
                    src={mainSrc}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-[10px] text-[#9CA3AF]">
                    No img
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="my-0 text-[15px] font-semibold leading-snug text-[#111827]">
                  {detail.name}
                </p>

                <p className="my-0 mt-1 text-[13px] font-semibold text-[#111827]">
                  {detail.price}
                  <span className="mx-2 font-normal text-[#D1D5DB]">|</span>
                  <span className="font-normal text-[#6B7280]">
                    {detail.stock} in stock
                  </span>
                </p>
              </div>
            </div>

            {deleteError ? (
              <p className="mx-6 mt-3 text-[13px] text-[#DC2626]">
                {deleteError}
              </p>
            ) : null}

            <div className="flex justify-end gap-3 px-6 py-4">
              <MaButton
                title="Cancel"
                onClick={() => setDeleteModalOpen(false)}
                disabled={deleteSubmitting}
                sx={{
                  bgcolor: "#FFFFFF",
                  color: "#374151",
                  border: "1px solid #E5E7EB",
                  boxShadow: "none",
                  minHeight: "44px",
                  minWidth: "100px",
                  borderRadius: "12px",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#F9FAFB" },
                }}
              />
              <MaButton
                title={deleteSubmitting ? "Deleting…" : "Delete permanently"}
                onClick={handleConfirmDelete}
                disabled={deleteSubmitting}
                sx={{
                  bgcolor: "#DC2626",
                  color: "#FFFFFF",
                  border: "none",
                  boxShadow: "none",
                  minHeight: "44px",
                  minWidth: "140px",
                  borderRadius: "12px",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#B91C1C" },
                  "&:disabled": { opacity: 0.7 },
                }}
              />
            </div>
          </div>
        </RsModal>
      ) : null}
    </Drawer>
  );
}
